/**
 * @file leadPalette.js
 * @description Single source of truth for the lead-dashboard "opportunity color
 * identity" — the colors of the KPI filter cards and their matching markers.
 *
 * `components/dashboard/KPIMetricsBar.js` (the KPI cards, which ARE the
 * dashboard's tab control since the LeadTabs pill row was removed 2026-08-20)
 * and `components/leads/LeadCard.js` (the NEW_BADGE_RED dot) import from here:
 *
 *  - HOT         → turquoise  (the brand primary; "Hot Opportunities")
 *  - REPUTATION  → dusty rose (Solo-accent pastel; "Build Reputation")
 *  - ALL         → a turquoise→rose gradient that spans the two (the "All"
 *                  KPI card reads as the union of the two buckets)
 *  - GREEN       → emerald ("Contacted" — done / made contact)
 *
 * Framework-agnostic: plain values only, no MUI imports (mirrors tokens.js).
 *
 * Currently consumed: `border`, `stops`, `tintHover`, `frameGradient`,
 * `numberGradient`, `text`/`textActive`, and NEW_BADGE_RED. The `tint` and
 * `glow` entries lost their last consumer on 2026-09-16 when the KPI bar's
 * SELECTED state was dimmed onto its own ink-toned fill/ring/glow (owner:
 * the pressed cards were "shiny"); they are kept unchanged so that change is
 * a one-file revert. Nothing outside KPIMetricsBar.js reads these four
 * objects, so retuning them is a KPI-bar-local decision.
 */

// Turquoise — "Hot Opportunities". Carries a readable idle text tone, a soft
// tint + border at rest, and a filled gradient + glow when active.
// `stops` are the raw hex endpoints, so consumers (e.g. the KPI bar) can build a
// gradient in any direction without re-encoding the colors.
export const HOT = {
  text: "#0D9488",
  border: "rgba(64, 224, 208, 0.40)",
  tint: "rgba(64, 224, 208, 0.08)",
  tintHover: "rgba(64, 224, 208, 0.16)",
  stops: ["#40E0D0", "#00A896"],
  gradient: "linear-gradient(135deg, #40E0D0 0%, #00A896 100%)",
  glow: "0 4px 12px rgba(64, 224, 208, 0.26)",
};

// Dusty-rose pastel — "Build Reputation". A brighter, softer take on the
// Solo-plan accent (#BF5867, see PricingContext) so the rose blends gently with
// the dashboard rather than reading as a harsh alert red.
export const REPUTATION = {
  text: "#C56C7A",
  border: "rgba(197, 108, 122, 0.32)",
  tint: "rgba(197, 108, 122, 0.07)",
  tintHover: "rgba(197, 108, 122, 0.14)",
  stops: ["#EFA1AD", "#CB6E7D"],
  gradient: "linear-gradient(135deg, #EFA1AD 0%, #CB6E7D 100%)",
  glow: "0 4px 12px rgba(203, 110, 125, 0.22)",
};

// Emerald green — currently used by the "Contacted" (נוצר קשר) KPI card, where
// green reads as "done / made contact". Same shape as HOT/REPUTATION.
export const GREEN = {
  text: "#059669",
  border: "rgba(16, 185, 129, 0.40)",
  tint: "rgba(16, 185, 129, 0.08)",
  tintHover: "rgba(16, 185, 129, 0.16)",
  stops: ["#34D399", "#059669"],
  gradient: "linear-gradient(135deg, #34D399 0%, #059669 100%)",
  glow: "0 4px 12px rgba(16, 185, 129, 0.26)",
};

// "All" — the union bucket. Its `stops` are the Hot stops followed by the
// Reputation stops, so the gradient is literally "made of the exact colors" of
// the two other cards (turquoise -> teal -> rose -> dusty-rose). This keeps the
// brightness in line with the siblings instead of muddying into a single dark
// teal→rose blend.
// Brighter blue-slate neutral for "All" (NOT a weak gray, NOT the childish
// turquoise→rose gradient). The KPI number uses the deeper, close-toned
// `numberGradient` below; the turquoise→rose link survives only as the LIGHT
// gradient frame/border on the idle card.
const ALL_NEUTRAL = "#6B7C91";

export const ALL = {
  neutral: ALL_NEUTRAL,
  text: ALL_NEUTRAL, // idle text tone
  textActive: "#4E6075", // darker slate — the active "All" card's check tick
  // Deep-slate duotone for the KPI number (close-toned, so it reads as ink with
  // a subtle teal lean rather than a flashy gradient).
  numberGradient: "linear-gradient(to left, #4E6075 0%, #456A7E 100%)",
  border: "rgba(120, 170, 165, 0.40)",
  // Very soft turquoise→rose tint (keeps a hint of the identity under the
  // neutral text). Was the ACTIVE "All" KPI card's fill until 2026-09-16, when
  // the selected state was dimmed (owner: the pressed cards were "shiny") and
  // moved onto the sibling cards' ink tones inside KPIMetricsBar. Kept for
  // future use — value unchanged, so restoring the old look is a one-line
  // revert there.
  tint: "linear-gradient(to left, rgba(64,224,208,0.10) 0%, rgba(203,110,125,0.10) 100%)",
  tintHover: "linear-gradient(to left, rgba(64,224,208,0.16) 0%, rgba(203,110,125,0.16) 100%)",
  stops: ["#40E0D0", "#00A896", "#EFA1AD", "#CB6E7D"],
  // LIGHT turquoise→rose gradient for the KPI card border — soft/pastel so it
  // reads as light as the siblings' translucent borders, not a heavy dark frame.
  frameGradient:
    "linear-gradient(to left, #A9EEE5 0%, #C7EFE9 35%, #F7D3D9 65%, #E7B2BB 100%)",
  // A touch stronger/more-saturated frame variant. Kept for future use — its
  // last consumer (the LeadTabs pills) was removed 2026-08-20.
  frameGradientStrong:
    "linear-gradient(to left, #84E5D8 0%, #A9E6DD 35%, #F2BDC6 65%, #DC9AA6 100%)",
  // The 4-stop blend (turquoise on the right toward Hot, rose on the left
  // toward Reputation), darker so white text stays legible on it. Kept for
  // future use — its last consumer (the LeadTabs pills) was removed 2026-08-20.
  gradientBright:
    "linear-gradient(to left, #45DECB 0%, #0AAF9E 33%, #EEA6B1 66%, #D17C8B 100%)",
  glow: "0 4px 12px rgba(120, 150, 160, 0.24)",
};

// Shared red used by the "new since last login" notification badges + the
// per-card new-opportunity dot. One hex so every red marker matches.
export const NEW_BADGE_RED = "#EF4444";
